function percentile(sorted, p) {
  if (sorted.length === 0) return 0;
  // Nearest-rank percentile per the standard form: ceil(p/100 * n) - 1, clamped.
  // Matches the formula used in src/probes/websocket.js.
  const idx = Math.min(sorted.length - 1, Math.max(0, Math.ceil((p / 100) * sorted.length) - 1));
  return sorted[idx];
}

function makeAbortError() {
  const err = new Error('request timed out');
  err.name = 'AbortError';
  return err;
}

function abortable(promise, signal) {
  if (!signal) return promise;
  if (signal.aborted) return Promise.reject(makeAbortError());
  return new Promise((resolve, reject) => {
    const onAbort = () => {
      cleanup();
      reject(makeAbortError());
    };
    const cleanup = () => signal.removeEventListener('abort', onAbort);
    signal.addEventListener('abort', onAbort, { once: true });
    promise.then(
      value => {
        cleanup();
        resolve(value);
      },
      err => {
        cleanup();
        reject(err);
      }
    );
  });
}

export async function fetchWithTimeout(url, {
  fetchImpl = fetch,
  timeoutMs = 2_000,
  options = {}
} = {}) {
  if (!Number.isFinite(timeoutMs) || timeoutMs <= 0 || typeof AbortController === 'undefined') {
    return fetchImpl(url, options);
  }
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeoutMs);
  try {
    return await abortable(
      fetchImpl(url, { ...options, signal: controller.signal }),
      controller.signal
    );
  } finally {
    clearTimeout(timeoutId);
  }
}

export async function runHttpPing({
  serverUrl,
  durationMs,
  intervalMs,
  requestTimeoutMs = Math.max(1_000, Math.min(3_000, intervalMs * 4)),
  fetchImpl = fetch,
  sleepImpl = ms => new Promise(r => setTimeout(r, ms)),
  onSample
}) {
  const samples = [];
  let errors = 0;
  const deadline = Date.now() + durationMs;

  while (Date.now() < deadline) {
    const t0 = Date.now();
    try {
      const res = await fetchWithTimeout(`${serverUrl}/ping?t=${t0}`, {
        fetchImpl,
        timeoutMs: Math.min(requestTimeoutMs, Math.max(1, deadline - Date.now())),
        options: { cache: 'no-store' }
      });
      if (!res.ok) {
        errors += 1;
      } else {
        await res.json();
        const rtt = Date.now() - t0;
        samples.push(rtt);
        if (onSample) onSample(rtt);
      }
    } catch {
      errors += 1;
    }
    const waitMs = Math.min(intervalMs, Math.max(0, deadline - Date.now()));
    if (waitMs > 0) await sleepImpl(waitMs);
  }

  const sorted = [...samples].sort((a, b) => a - b);
  const p50 = percentile(sorted, 50);
  const p95 = percentile(sorted, 95);
  const min = sorted[0] ?? 0;
  const max = sorted[sorted.length - 1] ?? 0;
  const mean = samples.length ? samples.reduce((a, b) => a + b, 0) / samples.length : 0;

  return {
    samples,
    count: samples.length,
    errors,
    minMs: min,
    maxMs: max,
    meanMs: mean,
    medianMs: p50,
    p50Ms: p50,
    p95Ms: p95
  };
}
